import React, { useEffect, useState } from 'react'
import api from '@/services/api'

/**
 * Research page — operator UI for the PII tokenisation study.
 *
 * Three tabs:
 *   - Runs: list + status + start a new run
 *   - Dataset: list locked + draft Golden Dataset tasks
 *   - Reports: render the Plan §13.4 markdown for a completed run
 */

type RunSummary = {
  run_id: string
  description: string
  pinned_model: string
  matrices: string[]
  dataset_version: string
  dataset_hash: string
  started_at: string
  finished_at: string | null
  status: string
  total_executions: number
  completed_executions: number
  failed_executions: number
}

type TaskListItem = {
  task_id: string
  vertical: string
  matrix: string
  psl_level: number
  tcm: string
  pii_categories: string[]
  locked: boolean
  reviewer_count: number
}

type Tab = 'runs' | 'dataset' | 'reports'

export default function ResearchPage() {
  const [tab, setTab] = useState<Tab>('runs')

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">PII Tokenisation Research</h1>
        <p className="text-gray-500 text-sm">
          Operator surface for the research plan. Start runs, monitor progress, render reports.
        </p>
      </div>

      <div className="border-b mb-4">
        <nav className="flex space-x-4">
          {(['runs', 'dataset', 'reports'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-2 text-sm font-medium border-b-2 ${
                tab === t
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'runs' && <RunsTab />}
      {tab === 'dataset' && <DatasetTab />}
      {tab === 'reports' && <ReportsTab />}
    </div>
  )
}

// ── Runs tab ────────────────────────────────────────────────────────────────

function RunsTab() {
  const [runs, setRuns] = useState<RunSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showNewRun, setShowNewRun] = useState(false)

  const refresh = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/research/runs')
      setRuns(data)
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    const t = setInterval(refresh, 15000)
    return () => clearInterval(t)
  }, [])

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium">Study runs</h2>
        <button
          onClick={() => setShowNewRun(true)}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm"
        >
          New run
        </button>
      </div>
      {showNewRun && <NewRunForm onClose={() => setShowNewRun(false)} onCreated={refresh} />}
      {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
      {loading && <div className="text-gray-500 text-sm">Loading…</div>}
      {!loading && runs.length === 0 && <div className="text-gray-400 text-sm">No runs yet.</div>}
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="text-left p-2">Run</th>
            <th className="text-left p-2">Status</th>
            <th className="text-left p-2">Model</th>
            <th className="text-left p-2">Matrices</th>
            <th className="text-right p-2">Executions</th>
            <th className="text-left p-2">Started</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.run_id} className="border-t hover:bg-gray-50">
              <td className="p-2 font-mono text-xs">{r.run_id}</td>
              <td className="p-2">
                <span className={`px-2 py-0.5 rounded text-xs ${statusColor(r.status)}`}>
                  {r.status}
                </span>
              </td>
              <td className="p-2 font-mono text-xs">{r.pinned_model}</td>
              <td className="p-2">{r.matrices.join(', ')}</td>
              <td className="p-2 text-right">
                {r.completed_executions} / {r.total_executions}
                {r.failed_executions > 0 && (
                  <span className="text-red-600 ml-1">({r.failed_executions} failed)</span>
                )}
              </td>
              <td className="p-2 text-gray-500">{new Date(r.started_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function statusColor(s: string): string {
  switch (s) {
    case 'queued':
      return 'bg-gray-200 text-gray-700'
    case 'running':
      return 'bg-blue-100 text-blue-700'
    case 'completed':
      return 'bg-green-100 text-green-700'
    case 'aborted':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

function NewRunForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [description, setDescription] = useState('')
  const [pinnedModel, setPinnedModel] = useState('claude-opus-4-7')
  const [matrices, setMatrices] = useState('1,2,3')
  const [repetitions, setRepetitions] = useState(5)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await api.post('/research/runs', {
        description,
        pinned_model: pinnedModel,
        matrices: matrices.split(',').map((s) => s.trim()).filter(Boolean),
        repetitions,
      })
      onCreated()
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="border rounded p-4 mb-4 bg-blue-50">
      <h3 className="font-medium mb-2">Start a new run</h3>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <label className="block">
          <span className="text-gray-600">Description</span>
          <input
            className="block w-full mt-1 px-2 py-1 border rounded"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-gray-600">Pinned model</span>
          <input
            className="block w-full mt-1 px-2 py-1 border rounded font-mono text-xs"
            value={pinnedModel}
            onChange={(e) => setPinnedModel(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-gray-600">Matrices (comma-separated)</span>
          <input
            className="block w-full mt-1 px-2 py-1 border rounded"
            value={matrices}
            onChange={(e) => setMatrices(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-gray-600">Repetitions</span>
          <input
            type="number"
            min={1}
            max={20}
            className="block w-full mt-1 px-2 py-1 border rounded"
            value={repetitions}
            onChange={(e) => setRepetitions(Number(e.target.value))}
          />
        </label>
      </div>
      {error && <div className="mt-2 text-red-600 text-sm">{error}</div>}
      <div className="mt-3 flex space-x-2">
        <button
          onClick={submit}
          disabled={submitting}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
        >
          {submitting ? 'Queuing…' : 'Queue run'}
        </button>
        <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── Dataset tab ─────────────────────────────────────────────────────────────

function DatasetTab() {
  const [tasks, setTasks] = useState<TaskListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [vertical, setVertical] = useState<string>('')
  const [lockedFilter, setLockedFilter] = useState<string>('')

  const refresh = async () => {
    setLoading(true)
    try {
      const params: any = {}
      if (vertical) params.vertical = vertical
      if (lockedFilter === 'locked') params.locked = true
      if (lockedFilter === 'draft') params.locked = false
      const { data } = await api.get('/research/dataset/tasks', { params })
      setTasks(data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [vertical, lockedFilter])

  return (
    <div>
      <div className="flex items-center mb-3 space-x-3 text-sm">
        <select
          value={vertical}
          onChange={(e) => setVertical(e.target.value)}
          className="border rounded px-2 py-1"
        >
          <option value="">All verticals</option>
          <option value="medical">Medical</option>
          <option value="educational">Educational</option>
          <option value="financial">Financial</option>
        </select>
        <select
          value={lockedFilter}
          onChange={(e) => setLockedFilter(e.target.value)}
          className="border rounded px-2 py-1"
        >
          <option value="">All</option>
          <option value="locked">Locked</option>
          <option value="draft">Draft</option>
        </select>
      </div>
      {loading && <div className="text-gray-500 text-sm">Loading…</div>}
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="text-left p-2">Task</th>
            <th className="text-left p-2">Vertical</th>
            <th className="text-left p-2">Matrix</th>
            <th className="text-left p-2">PSL</th>
            <th className="text-left p-2">TCM</th>
            <th className="text-left p-2">Categories</th>
            <th className="text-right p-2">Reviewers</th>
            <th className="text-left p-2">Locked</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={t.task_id} className="border-t hover:bg-gray-50">
              <td className="p-2 font-mono text-xs">{t.task_id}</td>
              <td className="p-2">{t.vertical}</td>
              <td className="p-2">{t.matrix}</td>
              <td className="p-2">{t.psl_level}</td>
              <td className="p-2 font-mono text-xs">{t.tcm}</td>
              <td className="p-2 text-xs">{t.pii_categories.join(', ')}</td>
              <td className="p-2 text-right">{t.reviewer_count}</td>
              <td className="p-2">
                {t.locked ? (
                  <span className="text-green-700">✓ locked</span>
                ) : (
                  <span className="text-gray-500">draft</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Reports tab ─────────────────────────────────────────────────────────────

function ReportsTab() {
  const [runId, setRunId] = useState('')
  const [markdown, setMarkdown] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchReport = async () => {
    if (!runId) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get(`/research/runs/${runId}/report`, {
        responseType: 'text',
      })
      setMarkdown(data)
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e))
      setMarkdown('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="flex items-center space-x-2 mb-3 text-sm">
        <input
          placeholder="run-1714400000-1"
          value={runId}
          onChange={(e) => setRunId(e.target.value)}
          className="border rounded px-2 py-1 font-mono w-80"
        />
        <button
          onClick={fetchReport}
          disabled={loading || !runId}
          className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Render report'}
        </button>
      </div>
      {error && <div className="text-red-600 text-sm">{error}</div>}
      {markdown && (
        <pre className="border rounded p-3 bg-gray-50 text-xs whitespace-pre-wrap leading-snug">
          {markdown}
        </pre>
      )}
    </div>
  )
}
